import React, { useState } from 'react';
import { Helmet } from 'react-helmet';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    services: [],
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleServiceChange = (serviceName) => {
    setFormData((prev) => {
      const selected = prev.services.includes(serviceName);
      return {
        ...prev,
        services: selected
          ? prev.services.filter((s) => s !== serviceName)
          : [...prev.services, serviceName],
      };
    });
  };

  const handleSubmit = (e) => {
    if (formData.services.length === 0) {
      e.preventDefault(); 
      return;
    }
  };

  return (
    <main
      className="relative flex min-h-screen items-center justify-center
      bg-gradient-to-b from-sky-50 via-indigo-50 to-white px-6 pt-32 pb-16 lg:px-8"
    >
      {/* SEO */}
      <Helmet>
        <title>Kontakta oss | Nordic Route</title>
        <meta
          name="description"
          content="Kontakta Nordic Route för frågor om logistik, transporter och samarbeten. Fyll i formuläret så återkommer vi snabbt."
        />
        <link rel="canonical" href="https://www.nordicroute.se/contact" />
      </Helmet>

      {/* Background */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 -top-64 -z-10 overflow-hidden blur-3xl"
      >
        <div
          className="relative left-1/2 aspect-[1108/632] w-[72rem] -translate-x-1/2
          bg-[radial-gradient(circle_at_top,_rgba(79,70,229,0.25),_transparent_60%),radial-gradient(circle_at_bottom,_rgba(56,189,248,0.25),_transparent_55%)]"
        />
      </div>

      {/* Form container */}
      <div className="relative z-10 w-full max-w-md">
        <div className="rounded-xl border border-slate-200 bg-white shadow-lg p-8 sm:p-10">
          <div className="mb-6 text-center">
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              Kontakta oss
            </h1>
            <p className="mt-3 text-sm text-slate-600">
              Fyll i formuläret så återkommer vi så snart som möjligt.
            </p>
          </div>

          <form
            action="https://formspree.io/f/xreglaql"
            method="POST"
            onSubmit={handleSubmit}
            className="space-y-5"
          >
            <input
              type="hidden"
              name="services"
              value={formData.services.join(', ')}
            />

            {/* Services */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-slate-700">
                Vilken tjänst gäller ditt ärende?  <span className="text-red-600">*</span>
              </label>

              <div className="space-y-2">
                {['Godstransport', 'Expressleveranser', 'Specialtransporter'].map(
                  (service) => (
                    <label
                      key={service}
                      className="flex items-center gap-3 text-sm text-slate-700"
                    >
                      <input
                        type="checkbox"
                        checked={formData.services.includes(service)}
                        onChange={() => handleServiceChange(service)}
                        className="h-4 w-4 rounded border-slate-300 text-indigo-600
                        focus:ring-2 focus:ring-indigo-500/30"
                      />
                      {service}
                    </label>
                  )
                )}
              </div>
              {formData.services.length === 0 && (
              <p className="text-sm text-red-600">
              Välj minst en tjänst för att fortsätta
                   </p>
              )}

            </div>

            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-slate-700">
                Fullständigt namn  <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                onChange={handleChange}
                placeholder="Ditt namn"
                className="mt-1.5 block w-full rounded-lg border border-slate-300
                bg-white px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400
                focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700">
                E-postadress <span className="text-red-600">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                onChange={handleChange}
                placeholder="din@epost.se"
                className="mt-1.5 block w-full rounded-lg border border-slate-300
                bg-white px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400
                focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30"
              />
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-slate-700">
                Telefonnummer
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                onChange={handleChange}
                placeholder="+46 (0)70 123 45 67"
                className="mt-1.5 block w-full rounded-lg border border-slate-300
                bg-white px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400
                focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30"
              />
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-slate-700">
                Meddelande
              </label>
              <textarea
                id="message"
                name="message"
                rows="4"
                onChange={handleChange}
                placeholder="Skriv ditt meddelande här..."
                className="mt-1.5 block w-full rounded-lg border border-slate-300
                bg-white px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400
                focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30"
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="mt-6 w-full rounded-lg bg-indigo-600 px-6 py-3
              text-sm font-semibold text-white shadow-md
              transition hover:bg-indigo-700"
            >
              Skicka meddelande
            </button>
          </form>
        </div>
      </div>
    </main>
  );
};

export default ContactPage;
